import { useUIStore } from '@/stores/ui/ui-store';

/**
 * Left nav rail width — mirrors `Sidebar.tsx` (`w-18` / `w-64`).
 * Keep in sync with that component and with `<main>`'s `pl-18` / `pl-64`.
 */
export const LEFT_NAV_WIDTH_PX = { collapsed: 72, expanded: 256 } as const;

/**
 * Right Filters/Columns rail width — mirrors `BaseFilterSidebar.tsx` and
 * `inventory/FilterSidebar.tsx` (`w-18` / `w-72`). Keep in sync with those and
 * with `<main>`'s `pr-18` / `pr-72`.
 */
export const FILTER_SIDEBAR_WIDTH_PX = { collapsed: 72, expanded: 288 } as const;

/** Default breathing room between a floating element and the rail beside it. */
export const SHELL_GUTTER_PX = 16;

/** The scroll-to-top button's own corner spacing (it sits at that offset in both axes). */
export const SCROLL_TOP_BUTTON_GUTTER_PX = 32;

/**
 * Corner lane the scroll-to-top button occupies: its gutter plus its 40px diameter
 * (`p-2.5` around a 20px icon). The floating bars add this to their own gutter while
 * the button is on screen, so they shrink out of its way instead of clipping its
 * corner. Vertical separation is not an option there: below the bars' 61px offset
 * lies the sticky totals strip, above it the bars themselves — and the button's
 * toast lift parks it inside that same band.
 */
export const SCROLL_TOP_BUTTON_LANE_PX = SCROLL_TOP_BUTTON_GUTTER_PX + 40;

export interface ShellInsets {
  /** px to keep clear on the left (left nav + gutter). */
  left: number;
  /** px to keep clear on the right (filter rail, when shown, + gutter). */
  right: number;
}

/**
 * Horizontal insets of the app-shell content band — the strip of viewport left
 * over between the left nav and the right Filters/Columns rail.
 *
 * `position: fixed` children ignore `<main>`'s `pl-*`/`pr-*` padding (their
 * containing block is the viewport), so any floating element that wants to sit
 * inside the content band has to reproduce those offsets itself. Applying the
 * returned values as `left` / `right` does that: with a `w-fit mx-auto` box the
 * insets both centre it in the band AND clamp it to the band's width, because
 * `width: fit-content` shrinks to the space left by `left`/`right`.
 *
 * Both rails are `fixed` and z-index 9998/9999 — far above any floating bar —
 * so an element that overflows the band is painted over, not just off-centre.
 *
 * The right rail contributes only while `showFilterSidebar` is on — the same
 * single-flag gate `Toast.tsx` uses. `<main>`'s `pr-*` is gated on that flag AND
 * on the route (`isInventoryPage || isTradesPage || isCounterpartiesPage`, see
 * `Layout.tsx`), because the flag defaults to `true` and the rail-less routes
 * (`/billing`, `/collateral`, `/trades/$tradeId`, `/`) never clear it. These
 * insets are therefore only sound for elements that live on a rail-bearing page
 * — every current caller does; anything floating on another route would have to
 * take the route into account as well.
 *
 * `gutterPx` lets a caller keep its own corner spacing — the scroll-to-top
 * button sits 32px off the edge (`bottom-8`), so it asks for 32 rather than
 * hugging the rail at the 16px default the centred bars use.
 */
export const useShellInsets = (gutterPx: number = SHELL_GUTTER_PX): ShellInsets => {
  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed);
  const showFilterSidebar = useUIStore((s) => s.showFilterSidebar);
  const filterSidebarCollapsed = useUIStore((s) => s.filterSidebarCollapsed);

  const leftRail = sidebarCollapsed ? LEFT_NAV_WIDTH_PX.collapsed : LEFT_NAV_WIDTH_PX.expanded;
  const rightRail = !showFilterSidebar
    ? 0
    : filterSidebarCollapsed
      ? FILTER_SIDEBAR_WIDTH_PX.collapsed
      : FILTER_SIDEBAR_WIDTH_PX.expanded;

  return {
    left: leftRail + gutterPx,
    right: rightRail + gutterPx,
  };
};
